import { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Settings,
  AlertCircle,
  AlarmClock,
  Eye,
  Bell,
  Palette,
  Lock,
  Shield,
  LogOut,
} from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import AppShellPage from '@components/layout/AppShellPage.js';
import MetricCard from '@components/ui/MetricCard.js';
import SurfaceCard from '@components/ui/SurfaceCard.js';
import Select from '@components/ui/Select.js';
import Switch from '@components/ui/Switch.js';
import Button from '@components/ui/Button.js';
import {
  usePolicyStatsQuery,
  useSettingsQuery,
  useUpdateSettingsMutation,
} from '@features/policies/hooks/usePoliciesQuery.js';
import { useLogoutMutation } from '@features/auth/hooks/useAuth.js';

const stagger = {
  hidden: {},
  show: { transition: { staggerChildren: 0.07 } },
};
const fadeUp = {
  hidden: { opacity: 0, y: 12 },
  show: { opacity: 1, y: 0, transition: { type: 'spring' as const, stiffness: 380, damping: 28 } },
};

const firstAlertOptions = [3, 5, 7, 10, 14].map((v) => ({
  value: String(v),
  label: `${String(v)} days`,
}));
const secondAlertOptions = [1, 2, 3].map((v) => ({
  value: String(v),
  label: `${String(v)} day${v > 1 ? 's' : ''}`,
}));
const themeOptions = [
  { value: 'light', label: 'Light' },
  { value: 'dark', label: 'Dark' },
  { value: 'system', label: 'System' },
];

export default function SettingsPage() {
  const history = useHistory();
  const { data: settingsData } = useSettingsQuery();
  const { data: statsData } = usePolicyStatsQuery();
  const updateMutation = useUpdateSettingsMutation();
  const logoutMutation = useLogoutMutation();

  const settingsObj = settingsData?.data;
  const stats = statsData?.data;

  const [offsets, setOffsets] = useState<number[]>([7, 1]);
  const [appLock, setAppLock] = useState(false);
  const [theme, setTheme] = useState('light');

  useEffect(() => {
    if (settingsObj) {
      setOffsets(settingsObj.reminderOffsets);
      setAppLock(settingsObj.appLockEnabled);
      setTheme(settingsObj.theme);
    }
  }, [settingsObj]);

  function saveOffsets(first: number, second: number) {
    const nextOffsets = [first, second];
    setOffsets(nextOffsets);
    updateMutation.mutate(
      { reminderOffsets: nextOffsets },
      {
        onSuccess: () => {
          toast.success('Reminder cadence saved');
        },
        onError: () => {
          toast.error('Failed to save settings');
        },
      },
    );
  }

  function toggleLock() {
    const next = !appLock;
    setAppLock(next);
    updateMutation.mutate(
      { appLockEnabled: next },
      {
        onSuccess: () => {
          toast.success(next ? 'App lock enabled' : 'App lock disabled');
        },
        onError: () => {
          toast.error('Failed to save settings');
        },
      },
    );
  }

  function saveTheme(nextTheme: string) {
    setTheme(nextTheme);
    updateMutation.mutate(
      { theme: nextTheme },
      {
        onSuccess: () => {
          toast.success(`Theme changed to ${nextTheme}`);
        },
        onError: () => {
          toast.error('Failed to save settings');
        },
      },
    );
  }

  return (
    <AppShellPage
      icon={Settings}
      title="System Settings"
      subtitle="Control reminder timing, workspace appearance, and secure access preferences."
    >
      {/* KPI row */}
      {stats && (
        <motion.div
          variants={stagger}
          initial="hidden"
          animate="show"
          className="grid grid-cols-2 gap-3 sm:gap-4 sm:grid-cols-4 auto-rows-fr"
        >
          {[
            {
              label: 'Portfolio',
              value: stats.total,
              desc: 'Policies tracked',
              icon: Settings,
              tone: 'accent' as const,
            },
            {
              label: 'Overdue',
              value: stats.overdue,
              desc: 'Immediate follow-up',
              icon: AlertCircle,
              tone: 'critical' as const,
            },
            {
              label: 'Due 7d',
              value: stats.due7,
              desc: 'Short-term renewals',
              icon: AlarmClock,
              tone: 'warning' as const,
            },
            {
              label: 'Due 30d',
              value: stats.due30,
              desc: 'Upcoming pipeline',
              icon: Eye,
              tone: 'success' as const,
            },
          ].map((kpi) => (
            <motion.div key={kpi.label} variants={fadeUp}>
              <MetricCard
                label={kpi.label}
                value={kpi.value}
                description={kpi.desc}
                icon={kpi.icon}
                tone={kpi.tone}
              />
            </motion.div>
          ))}
        </motion.div>
      )}

      {/* Settings cards */}
      <motion.div
        variants={stagger}
        initial="hidden"
        animate="show"
        className="mt-5 sm:mt-6 lg:mt-8 grid grid-cols-1 gap-4 sm:gap-6 md:grid-cols-2 items-stretch auto-rows-fr"
      >
        {/* Reminder cadence */}
        <motion.div variants={fadeUp}>
          <SurfaceCard
            eyebrow="Automation"
            title="Reminder cadence"
            description="Keep reminder timing predictable across the renewal workflow."
          >
            <div className="space-y-3">
              <SettingsRow
                icon={<Bell size={16} className="text-slate" />}
                title="First renewal alert"
                description="Initial reminder before the policy end date."
              >
                <Select
                  value={String(offsets[0] ?? 7)}
                  onValueChange={(v) => {
                    saveOffsets(Number(v), offsets[1] ?? 1);
                  }}
                  options={firstAlertOptions}
                  className="w-28"
                />
              </SettingsRow>

              <SettingsRow
                icon={<Bell size={16} className="text-amber-fg" />}
                title="Final renewal alert"
                description="Last escalation before expiry."
              >
                <Select
                  value={String(offsets[1] ?? 1)}
                  onValueChange={(v) => {
                    saveOffsets(offsets[0] ?? 7, Number(v));
                  }}
                  options={secondAlertOptions}
                  className="w-28"
                />
              </SettingsRow>
            </div>
          </SurfaceCard>
          {/* Info card */}
          <SurfaceCard className=" mt-4 border-dashed bg-paper/70">
            <div className="flex items-start gap-3 text-left">
              <div className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-surface text-slate border border-line">
                <Shield size={16} strokeWidth={1.75} />
              </div>
              <div>
                <p className="text-sm font-bold text-ink">Server-backed preferences</p>
                <p className="mt-1 text-sm leading-6 text-ink-faint">
                  Reminder offsets and theme changes sync to the backend immediately so the wider
                  workflow stays aligned.
                </p>
              </div>
            </div>
          </SurfaceCard>
        </motion.div>

        {/* Right column */}
        <motion.div variants={fadeUp} className="space-y-6">
          {/* Appearance */}
          <SurfaceCard
            eyebrow="Workspace"
            title="Appearance"
            description="Choose how the app should render throughout the workday."
          >
            <SettingsRow
              icon={<Palette size={16} className="text-violet-500" />}
              title="Application theme"
              description="Switch between light, dark, or system sync."
            >
              <Select
                value={theme}
                onValueChange={saveTheme}
                options={themeOptions}
                className="w-32"
              />
            </SettingsRow>
          </SurfaceCard>

          {/* Security */}
          <SurfaceCard
            eyebrow="Security"
            title="Access protection"
            description="Keep the renewal register private on shared devices."
          >
            <SettingsRow
              icon={<Lock size={16} className="text-red-fg" />}
              title="Require biometric or PIN lock"
              description="Prompt for secure entry when the app launches."
            >
              <Switch checked={appLock} onCheckedChange={toggleLock} id="app-lock-toggle" />
            </SettingsRow>
          </SurfaceCard>
        </motion.div>
      </motion.div>

      {/* Sign out */}
      <motion.div variants={fadeUp} initial="hidden" animate="show" className="mt-6 sm:mt-8">
        <SurfaceCard
          eyebrow="Account"
          title="Sign out"
          description="End your current session. You'll be redirected to the login screen."
        >
          <Button
            variant="destructive"
            size="lg"
            className="w-full"
            onClick={() => {
              void logoutMutation.mutateAsync().then(() => {
                history.push('/login');
              });
            }}
            loading={logoutMutation.isPending}
          >
            <LogOut size={16} className="mr-2" />
            {logoutMutation.isPending ? 'Signing out...' : 'Sign out'}
          </Button>
        </SurfaceCard>
      </motion.div>
    </AppShellPage>
  );
}

function SettingsRow({
  icon,
  title,
  description,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 rounded-2xl bg-paper/80 px-4 py-4">
      <div className="flex items-center gap-3 text-left min-w-0 flex-1">
        <span className="shrink-0">{icon}</span>
        <div className="min-w-0">
          <p className="text-sm font-bold text-ink">{title}</p>
          <p className="mt-0.5 text-xs text-ink-faint">{description}</p>
        </div>
      </div>
      <div className="shrink-0 self-end sm:self-auto">{children}</div>
    </div>
  );
}
